import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import "./Styles/SponsorsPage.css";
import "./Styles/GetInvolved.css";
import Footer from "./Footer";
import SponsorsTronArena from "./SponsorsTronArena";
import { getFormSubmitUrl, postFormJson } from "./formSubmitUrl";

const INITIAL_SPONSOR_FORM = {
  name: "",
  email: "",
  organization: "",
  partnershipGoals: "",
  comments: "",
};

/** Public assets (URL-encoded paths for filenames with spaces) */
const ASSET_TEAM_LAB_PHOTO = "/assets/team-sponsors-hero.png";
const SPONSORSHIP_PACKAGE_PDF = "/assets/DCRT-Sponsorship-Package-2025-2026.pdf";

/** Current sponsors — assets in public/assets/sponsor-logos */
const SPONSOR_LOGOS = [
  { name: "Ansys", src: "/assets/sponsor-logos/Ansys.png" },
  { name: "Engineers Nova Scotia", src: "/assets/sponsor-logos/Engineers_NS_CT.png" },
  { name: "Eastward Energy", src: "/assets/sponsor-logos/Eastward-Energy-Logo_Full-Colour.png" },
  { name: "R.D. Crane", src: "/assets/sponsor-logos/RD_CRANE_LOGO_W_TAGLINE_Final-_1_.png" },
  {
    name: "SolidWorks",
    src: "/assets/sponsor-logos/SolidWorks-Logo-1024x640.png",
    /** Wide aspect mark reads small at default cap; pad box unchanged */
    logoMaxHeightPx: 58,
  },
];

/** Columns match the 2025–2026 sponsorship package PDF */
const SPONSOR_TIER_COLUMNS = [
  { id: "war", name: "War Bond Holder", amount: "Under $250" },
  { id: "battle", name: "Battlefield Intelligence Provider", amount: "$250" },
  { id: "key", name: "Key Strategic Partner", amount: "$1,000" },
  { id: "prime", name: "Prime Contractor", amount: "$2,000+" },
];

const TIER_IDS = SPONSOR_TIER_COLUMNS.map((c) => c.id);

const SPONSOR_LEVEL_ROWS = [
  { label: "Team resumes on request", war: true, battle: true, key: true, prime: true },
  { label: "Supporter logo on team website", war: true, battle: true, key: true, prime: true },
  { label: "Social media post during the school year", war: true, battle: true, key: true, prime: true },
  { label: "Supporter logo on team apparel", war: false, battle: true, key: true, prime: true },
  { label: "Supporter logo on event fair banner", war: false, battle: false, key: true, prime: true },
  {
    label: "One of four logo spots on the top carbon fibre armour of the robot",
    war: false,
    battle: false,
    key: false,
    prime: true,
  },
  {
    label: "Social media post the week leading up to the competition",
    war: false,
    battle: false,
    key: false,
    prime: true,
  },
  { label: "Full company description on team website", war: false, battle: false, key: false, prime: true },
  {
    label: "One of two logo spaces on the robot’s weapon support uprights",
    war: false,
    battle: false,
    key: false,
    prime: true,
  },
];

/** Partner logos — equal tiles, alphabetical order (no implied contribution rank) */
function PartnerEqualGrid({ sponsors, motionFade, motionTransition, reduceMotion }) {
  const items = [...sponsors].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  return (
    <ul className="sponsors-partner-grid">
      {items.map((s, i) => (
        <motion.li
          key={s.name}
          className="sponsors-partner-cell"
          {...motionFade}
          transition={reduceMotion ? undefined : { ...motionTransition, delay: i * 0.05 }}
        >
          <div className="sponsors-partner-card">
            <span className="sponsors-partner-card__corner sponsors-partner-card__corner--tl" aria-hidden="true" />
            <span className="sponsors-partner-card__corner sponsors-partner-card__corner--tr" aria-hidden="true" />
            <span className="sponsors-partner-card__corner sponsors-partner-card__corner--bl" aria-hidden="true" />
            <span className="sponsors-partner-card__corner sponsors-partner-card__corner--br" aria-hidden="true" />
            <div className="sponsors-partner-card__pad">
              <img
                src={s.src}
                alt={`${s.name} logo`}
                loading="lazy"
                decoding="async"
                style={s.logoMaxHeightPx ? { maxHeight: `${s.logoMaxHeightPx}px` } : undefined}
              />
            </div>
          </div>
          <span className="sponsors-partner-name">{s.name}</span>
        </motion.li>
      ))}
    </ul>
  );
}

function LevelCheck({ included }) {
  if (included) {
    return (
      <span className="sponsors-level-check sponsors-level-check--yes" aria-label="Included">
        ✓
      </span>
    );
  }
  return <span className="sponsors-level-check sponsors-level-check--no" aria-label="Not included" />;
}

export default function SponsorsPage() {
  const reduceMotion = useReducedMotion();
  const formRef = useRef(null);

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    const firstInput = formRef.current?.querySelector("input, textarea, button");
    window.setTimeout(() => firstInput?.focus?.(), reduceMotion ? 0 : 400);
  }, [reduceMotion]);

  const scrollToLevels = useCallback(() => {
    document.getElementById("sponsor-levels")?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  }, [reduceMotion]);

  useEffect(() => {
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const script = document.createElement("script");
    script.src = "/particles.js";
    script.async = true;

    script.onload = () => {
      if (!window.particlesJS) return;

      const particleColor = "#7dd3fc";

      fetch("/assets/particles.json")
        .then((res) => res.json())
        .then((config) => {
          config.particles.number.value = Math.min(config.particles?.number?.value ?? 60, 64);
          config.particles.color.value = particleColor;
          config.particles.line_linked.color = "rgba(244, 63, 94, 0.12)";
          config.interactivity = config.interactivity || {};
          config.interactivity.detect_on = "canvas";
          config.interactivity.events = {
            onhover: { enable: true, mode: "grab" },
            onclick: { enable: false, mode: "push" },
            resize: true,
          };
          window.particlesJS("sponsors-particles-js", config);
        })
        .catch(() => {
          window.particlesJS.load("sponsors-particles-js", "/assets/particles.json");
        });
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      const el = document.getElementById("sponsors-particles-js");
      if (el) el.innerHTML = "";
    };
  }, []);

  const [formData, setFormData] = useState(() => ({ ...INITIAL_SPONSOR_FORM }));
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!submitSuccess) return;
    formRef.current?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  }, [submitSuccess, reduceMotion]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const payload = {
      name: formData.name,
      email: formData.email,
      university: formData.organization,
      team: ["Sponsorship"],
      fitReason: formData.partnershipGoals,
      comments: formData.comments,
      formSource: "sponsors",
    };

    setIsSubmitting(true);
    try {
      const { response, data } = await postFormJson(getFormSubmitUrl(), payload);
      if (response.ok) {
        setFormData({ ...INITIAL_SPONSOR_FORM });
        setSubmitSuccess(true);
      } else {
        alert(data.message || "Error submitting form");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("An error occurred while submitting the form.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSponsorSubmitAnother = () => {
    setFormData({ ...INITIAL_SPONSOR_FORM });
    setSubmitSuccess(false);
  };

  const motionTransition = { duration: 0.55, ease: [0.16, 1, 0.3, 1] };
  const motionFade = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "0px 0px -12% 0px" },
        transition: motionTransition,
      };

  return (
    <div className="sponsors-page">
      <div id="sponsors-particles-js" className="sponsors-particles" aria-hidden="true" />
      <div className="sponsors-grid" aria-hidden="true" />
      <div className="sponsors-grid sponsors-grid--accent" aria-hidden="true" />
      <div className="sponsors-combat-bg" aria-hidden="true">
        <SponsorsTronArena />
        <div className="sponsors-combat-bg__hud" />
      </div>
      <div className="sponsors-vignette" aria-hidden="true" />
      <div className="sponsors-scanline" aria-hidden="true" />

      <div className="sponsors-content">
        <section className="sponsors-hero sponsors-hero--arena" aria-labelledby="sponsors-hero-title">
          <div className="sponsors-hero-inner">
            <div className="sponsors-hero-glow" aria-hidden="true" />
            <p className="sponsors-hero-eyebrow">
              <span className="sponsors-hero-eyebrow-pulse" aria-hidden="true" />
              Arena feed · Sponsorship 2025–2026
            </p>
            <motion.h1 id="sponsors-hero-title" className="sponsors-hero-title" {...motionFade}>
              Put your brand in the fight
            </motion.h1>
            <motion.p className="sponsors-hero-sub" {...motionFade}>
              Fund the bot, the cage tests, and the run to NHRL — October 3 qualifying in Connecticut. Clear tiers, real hardware, and visibility
              that travels from the shop to the stream.
            </motion.p>
            <motion.div className="sponsors-hero-actions" {...motionFade}>
              <button type="button" className="dcrt-btn dcrt-btn--primary" onClick={scrollToForm}>
                Become a sponsor
              </button>
              <button type="button" className="dcrt-btn dcrt-btn--ghost" onClick={scrollToLevels}>
                View sponsorship levels
              </button>
              <a href={SPONSORSHIP_PACKAGE_PDF} className="dcrt-btn dcrt-btn--ghost" download>
                Download sponsorship package (PDF)
              </a>
            </motion.div>
            <p className="sponsors-hero-readout" aria-hidden="true">
              <span>
                Robot · <strong>T.O.N.E.</strong>
              </span>
              <span>│</span>
              <span>
                Class · <strong>3 lb NHRL</strong>
              </span>
              <span>│</span>
              <span>
                Team · <strong>~25 students</strong>
              </span>
            </p>
          </div>
        </section>

        <section className="sponsors-section sponsors-showcase" aria-labelledby="sponsors-showcase-heading">
          <div className="sponsors-showcase-inner sponsors-showcase-inner--pcb">
            <div className="sponsors-showcase-pcb-layer" aria-hidden="true" />
            <div className="sponsors-showcase-glow" aria-hidden="true" />
            <div className="sponsors-showcase-header">
              <p className="dcrt-section-label">Partners</p>
              <h2 id="sponsors-showcase-heading" className="dcrt-h2">
                Thanks to everyone who backs the build
              </h2>
              <p className="sponsors-lead">
                We’re a student-led team: every organization here gets the same spotlight on this page — no league tables, just genuine gratitude for
                tooling, materials, software, and goodwill that keep the machines running.
              </p>
            </div>

            <PartnerEqualGrid
              sponsors={SPONSOR_LOGOS}
              motionFade={motionFade}
              motionTransition={motionTransition}
              reduceMotion={reduceMotion}
            />

            <p className="sponsors-pcb-footnote">
              Interested in supporting us? Details on collaboration options are further down — this wall is only about saying thank you.
            </p>
          </div>
        </section>

        <section className="sponsors-section" aria-labelledby="sponsors-mission-heading">
          <div className="sponsors-section-header">
            <p className="dcrt-section-label">Who we are</p>
            <h2 id="sponsors-mission-heading" className="dcrt-h2">
              Mission &amp; combat robotics
            </h2>
          </div>
          <div className="sponsors-split">
            <motion.div className="sponsors-split-card dcrt-panel" {...motionFade}>
              <h3 className="sponsors-split-title">Our mission</h3>
              <p>
                Dalhousie Combat Robotics designs and builds fighting robots for competition while developing practical engineering skills: CAD,
                fabrication, electronics, and outreach that connect the classroom to the arena.
              </p>
            </motion.div>
            <motion.div className="sponsors-split-card dcrt-panel" {...motionFade} transition={reduceMotion ? undefined : { ...motionTransition, delay: 0.06 }}>
              <h3 className="sponsors-split-title">What is combat robotics?</h3>
              <p>
                Teams design remote-controlled machines to withstand high-energy impacts in enclosed arenas. Strategy, materials, and reliability
                are tested in seconds — the same discipline you expect from motorsport or aerospace prototyping, at student scale.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="sponsors-section sponsors-team-visual" aria-labelledby="sponsors-team-heading">
          <div className="sponsors-section-header">
            <h2 id="sponsors-team-heading" className="dcrt-h2">
              Team overview
            </h2>
          </div>
          <div className="sponsors-team-layout">
            <motion.figure className="sponsors-team-figure" {...motionFade}>
              <div className="sponsors-team-frame">
                <div className="sponsors-team-frame__bracket sponsors-team-frame__bracket--tl" aria-hidden="true" />
                <div className="sponsors-team-frame__bracket sponsors-team-frame__bracket--tr" aria-hidden="true" />
                <div className="sponsors-team-frame__bracket sponsors-team-frame__bracket--bl" aria-hidden="true" />
                <div className="sponsors-team-frame__bracket sponsors-team-frame__bracket--br" aria-hidden="true" />
                <img
                  src={ASSET_TEAM_LAB_PHOTO}
                  alt="Dalhousie Combat Robotics team members in the lab with T.O.N.E. on the display behind them"
                  width={1200}
                  height={675}
                  loading="lazy"
                  decoding="async"
                  className="sponsors-team-photo"
                />
                <div className="sponsors-team-scan" aria-hidden="true" />
              </div>
              <figcaption className="sponsors-team-caption">
                <span className="sponsors-team-caption__tag">FIELD UNIT</span> Dalhousie Combat Robotics — mechanical, electrical, and software leads
                in one room.
              </figcaption>
            </motion.figure>
            <motion.div
              className="dcrt-panel sponsors-narrative sponsors-team-copy"
              {...motionFade}
              transition={reduceMotion ? undefined : { ...motionTransition, delay: 0.08 }}
            >
              <p>
                Roughly twenty-five students work across mechanical, electrical, and software project teams. In our first year we proved we could
                run a serious build on deadline — funding for materials, tooling, and travel is now what limits how fast we iterate and how often we
                can compete.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="sponsors-section" aria-labelledby="sponsors-road-heading">
          <div className="sponsors-section-header">
            <p className="dcrt-section-label">Road ahead</p>
            <h2 id="sponsors-road-heading" className="dcrt-h2">
              NHRL qualifying · October 3
            </h2>
          </div>
          <motion.div className="dcrt-panel sponsors-narrative" {...motionFade}>
            <p>
              We are commissioning a prototype combat enclosure for safe drivetrain and weapon testing. The next major milestone is NHRL
              qualifying on <strong>October 3</strong> in Connecticut for our <strong>3 lb beetleweight</strong>, <em>T.O.N.E.</em>
            </p>
          </motion.div>
        </section>

        <section id="sponsor-levels" className="sponsors-section sponsors-levels-section" aria-labelledby="sponsors-levels-heading">
          <div className="sponsors-section-header sponsors-section-header--center">
            <p className="dcrt-section-label">Sponsorship levels</p>
            <h2 id="sponsors-levels-heading" className="dcrt-h2">
              Partnership tiers (2025–2026)
            </h2>
            <p className="sponsors-lead">
              Contribution tiers and benefits match our published package. In-kind software, materials, or services are mapped to these levels by
              agreed monetary value.
            </p>
          </div>

          <div className="sponsors-levels-table-wrap" role="region" aria-label="Sponsorship benefits by tier" tabIndex={0}>
            <table className="sponsors-levels-table">
              <thead>
                <tr>
                  <th scope="col" className="sponsors-levels-sticky-col">
                    Benefit
                  </th>
                  {SPONSOR_TIER_COLUMNS.map((c) => (
                    <th key={c.id} scope="col">
                      <span className="sponsors-levels-th-name">{c.name}</span>
                      <span className="sponsors-levels-th-amt">{c.amount}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SPONSOR_LEVEL_ROWS.map((row) => (
                  <tr key={row.label}>
                    <th scope="row" className="sponsors-levels-sticky-col">
                      {row.label}
                    </th>
                    {TIER_IDS.map((id) => (
                      <td key={id}>
                        <LevelCheck included={row[id]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="sponsors-cta-block dcrt-panel" aria-labelledby="sponsors-cta-heading">
          <p className="dcrt-section-label">Next step</p>
          <h2 id="sponsors-cta-heading">Ready to back the build?</h2>
          <p className="sponsors-lead">
            Send a short note with your organization and goals — we will follow up with a focused conversation aligned to the tier you have in
            mind.
          </p>
          <button type="button" className="dcrt-btn dcrt-btn--primary" onClick={scrollToForm}>
            Sponsorship inquiry
          </button>
        </section>

        <section ref={formRef} className="sponsors-form-section" aria-labelledby="sponsors-form-title">
          <h2 id="sponsors-form-title" className="sponsors-form-heading">
            Sponsorship inquiry
          </h2>
          {!submitSuccess && (
            <p className="sponsors-form-note">
              Submissions use the same secure pipeline as Get Involved. Your organization maps to the &quot;university&quot; field on our backend (no server
              change required).
            </p>
          )}
          <div className="form-container">
            {submitSuccess ? (
              <div className="sponsors-form-thanks dcrt-panel" role="status" aria-live="polite">
                <h3 className="sponsors-form-thanks-title">Thanks for your interest</h3>
                <p className="sponsors-form-thanks-body">
                  Thank you for your interest in becoming a sponsor. A member of our team will be in touch with you as soon as possible. You will also
                  receive a confirmation email — if it does not arrive within a few minutes, check your spam or junk folder.
                </p>
                <button type="button" className="submit-button sponsors-form-thanks-action" onClick={handleSponsorSubmitAnother}>
                  Submit another response
                </button>
              </div>
            ) : (
            <form className="join-form" onSubmit={handleSubmit}>
              <label htmlFor="sponsor-name">Name:</label>
              <input
                type="text"
                id="sponsor-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                autoComplete="name"
              />

              <label htmlFor="sponsor-email">Email:</label>
              <input
                type="email"
                id="sponsor-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />

              <label htmlFor="sponsor-organization">Organization / company:</label>
              <input
                type="text"
                id="sponsor-organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                required
                autoComplete="organization"
              />

              <label htmlFor="sponsor-goals">Sponsorship goals and partnership idea:</label>
              <textarea
                id="sponsor-goals"
                name="partnershipGoals"
                value={formData.partnershipGoals}
                onChange={handleChange}
                rows={5}
                required
              />

              <label htmlFor="sponsor-comments">Additional details (optional):</label>
              <textarea id="sponsor-comments" name="comments" value={formData.comments} onChange={handleChange} rows={3} />

              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? "Sending…" : "Submit inquiry"}
              </button>
            </form>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
